import {
  Activity as ActivityIcon,
  AlertTriangle,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Coffee,
  Eye,
  Globe,
  Hand,
  Laptop,
  MessageSquare,
  Plus,
  Puzzle,
  School,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { KID_COLORS, kidColor } from '../lib/palette';
import { api } from '../lib/api';
import { activityDetail, clock, dayLabel } from '../lib/format';
import { groupDevices, type Child, type Device, type FeedActivity, type Inbox } from '../lib/types';
import { Avatar } from '../ui/Avatar';
import { Sheet } from '../ui/Sheet';
import { QuickActions } from './Actions';
import { ParentTasks } from './ParentTasks';
import { planHas } from './Plans';
import { ComplianceNote } from './Compliance';
import { SetupChecklist } from './SetupChecklist';
import { useParent } from './ParentContext';
import { extOnline, PolicyBadge, Sharing } from './StatusBits';

const COLORS = KID_COLORS;

const FEED_ICONS: Record<string, React.ReactNode> = {
  site: <Globe size={15} />,
  alert: <AlertTriangle size={15} />,
  chat: <MessageSquare size={15} />,
  session: <School size={15} />,
  watch: <Eye size={15} />,
  device: <Laptop size={15} />,
  request: <Hand size={15} />,
  submit: <Check size={15} />,
  reading: <BookOpen size={15} />,
  break: <Coffee size={15} />,
};

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

interface DueItem {
  id: string;
  childId: string;
  childName: string;
  childColor: string;
  title: string;
  subject: string;
  dueDate: string;
}
/** `shown` is false when no child shows the Planner (hidden or off, #502); older servers leave it out. */
type DueAssignments = { dueToday: DueItem[]; overdue: DueItem[]; shown?: boolean };
type Drill = 'chats' | 'school' | 'devices' | 'due' | 'overdue';

/** Assignments not done that are due today, and overdue ones, across the family (#330). */
function useDueAssignments() {
  const { socket } = useParent();
  const [due, setDue] = useState<DueAssignments>({ dueToday: [], overdue: [] });
  const load = useCallback(() => {
    api.get<DueAssignments>('/api/assignments/due').then(setDue).catch(() => undefined);
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    if (!socket) return;
    const offs = ['activity', 'policy'].map((t) => socket.on(t, load));
    return () => offs.forEach((off) => off());
  }, [socket, load]);
  return due;
}

/** Unread chat + "done for today" requests (#169), kept fresh by the live socket. */
function useInbox() {
  const { socket } = useParent();
  const [inbox, setInbox] = useState<Inbox>({ chats: [], doneRequests: [] });
  const load = useCallback(() => {
    api.get<Inbox>('/api/inbox').then(setInbox).catch(() => undefined);
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    if (!socket) return;
    const offs = ['chat', 'done-request', 'done-answer', 'help-request', 'policy', 'connection'].map((t) => socket.on(t, load));
    return () => offs.forEach((off) => off());
  }, [socket, load]);
  return { inbox, reload: load };
}

export function Dashboard() {
  const { parent, children, loaded } = useParent();
  const [adding, setAdding] = useState(false);
  const { inbox, reload } = useInbox();
  const [drill, setDrill] = useState<Drill | null>(null);
  const due = useDueAssignments();
  // The first-run checklist (#552) replaces the empty-state steps while it's showing.
  const [checklist, setChecklist] = useState(false);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>
            {greeting()}, {parent.name.split(' ')[0]}
          </h1>
          <p className="muted">
            {children.length && !children.some((c) => planHas(c, 'computer'))
              ? 'Your homeschool at a glance.'
              : children.length
              ? `${children.filter((c) => c.policy.schoolTime).length} of ${children.length} in school time right now`
              : 'Add your first child to get started.'}
          </p>
        </div>
        <div className="row-gap page-head-actions">
          {children.length > 0 && (
            <button className="btn primary" onClick={() => setAdding(true)}>
              <Plus size={18} /> Add child
            </button>
          )}
        </div>
      </header>

      <SetupChecklist onAddChild={() => setAdding(true)} onShown={setChecklist} />

      {loaded && !children.length && !checklist && (
        <div className="empty card">
          <h2>Set up ClassGuardian in three steps</h2>
          <ol className="steps">
            <li>Add your child.</li>
            <li>Install ClassGuardian on their computer (their Devices tab shows how) and pair it with the code.</li>
            <li>Set their school hours. You can watch live, talk and check in whenever it’s school time.</li>
          </ol>
          <button className="btn primary big" onClick={() => setAdding(true)}>
            <Plus size={18} /> Add a child
          </button>
        </div>
      )}

      {!!inbox.doneRequests?.length && <DoneRequests requests={inbox.doneRequests} reload={reload} />}

      {children.length > 0 && <TodaySummary children={children} inbox={inbox} due={due} onDrill={setDrill} />}

      <div className="kid-grid">
        {children.map((c) => (
          <KidCard key={c.id} child={c} />
        ))}
      </div>

      {loaded && (!parent.plan || parent.plan.features.includes('todo')) && <ParentTasks />}

      {children.length > 0 && <RecentActivity />}

      {adding && <AddChildSheet onClose={() => setAdding(false)} />}
      {drill === 'chats' && <InboxSheet inbox={inbox} onClose={() => setDrill(null)} />}
      {(drill === 'due' || drill === 'overdue') && <DueSheet kind={drill} due={due} onClose={() => setDrill(null)} />}
      {(drill === 'school' || drill === 'devices') && <FamilySheet kind={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

function SummaryTile({
  value,
  label,
  tone,
  hue,
  onClick,
}: {
  value: number | string;
  label: string;
  tone?: string;
  /** Which of the theme's 8 colors the tile is (#755, #761). */
  hue?: number;
  onClick?: () => void;
}) {
  const style = hue === undefined ? undefined : { ['--tile' as string]: kidColor(KID_COLORS[hue]!) };
  const body = (
    <>
      <span className="summary-tile-value">{value}</span>
      <span className="summary-tile-label">{label}</span>
    </>
  );
  return onClick ? (
    <button className={`summary-tile clickable ${tone ?? ''}`} style={style} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className={`summary-tile ${tone ?? ''}`} style={style}>
      {body}
    </div>
  );
}

function TodaySummary({ children, inbox, due, onDrill }: { children: Child[]; inbox: Inbox; due: DueAssignments; onDrill: (k: Drill) => void }) {
  const unread = inbox.chats.reduce((n, c) => n + c.count, 0);
  const inSchool = children.filter((c) => c.policy.schoolTime).length;
  const devicesOnline = children.reduce((n, c) => n + c.devices.filter((d) => d.online).length, 0);
  // Records-only plans (#564): no computers, so no school time or devices to show.
  const computers = children.some((c) => planHas(c, 'computer'));
  return (
    <div className="summary-row">
      {computers && <SummaryTile value={inSchool} label="In school now" hue={0} tone={inSchool ? 'good' : ''} onClick={() => onDrill('school')} />}
      {computers && <SummaryTile value={devicesOnline} label="Devices online" hue={1} tone={devicesOnline ? 'good' : ''} onClick={() => onDrill('devices')} />}
      {due.shown !== false && (
        <>
          <SummaryTile
            value={due.dueToday.length}
            label={due.dueToday.length === 1 ? 'Assignment due today' : 'Assignments due today'}
            hue={2}
            onClick={() => onDrill('due')}
          />
          <SummaryTile
            value={due.overdue.length}
            label={due.overdue.length === 1 ? 'Overdue assignment' : 'Overdue assignments'}
            hue={3}
            tone={due.overdue.length ? 'attention' : ''}
            onClick={() => onDrill('overdue')}
          />
        </>
      )}
      {inbox.shown?.chat !== false && (
        <SummaryTile
          value={unread}
          label={unread === 1 ? 'Unread message' : 'Unread messages'}
          hue={4}
          tone={unread ? 'attention' : ''}
          onClick={() => onDrill('chats')}
        />
      )}
    </div>
  );
}

/**
 * "I'm done for today" (#440): the child finished early. End school time for the rest of the day (the schedule starts
 * again tomorrow), or say not yet. Shown at the top of Home; the push notification opens here.
 */
function DoneRequests({ requests, reload }: { requests: NonNullable<Inbox['doneRequests']>; reload: () => void }) {
  const { toast, replaceChild } = useParent();
  const [busy, setBusy] = useState<string | null>(null);
  async function answer(childId: string, name: string, approve: boolean) {
    setBusy(childId);
    try {
      const c = await api.post<Child>(`/api/children/${childId}/done-for-day`, { approve });
      replaceChild(c);
      toast({ text: approve ? `School’s done for today for ${name}. It starts again tomorrow.` : `Told ${name} to keep going.`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      reload();
      setBusy(null);
    }
  }
  return (
    <section className="card done-requests" aria-label="Done for today">
      {requests.map((r) => (
        <div key={r.activityId} className="done-request">
          <Avatar name={r.childName} color={r.childColor} size={40} />
          <span className="grow">
            <strong>{r.childName} says they’re done for today</strong>
            <span className="muted small block">
              {clock(r.ts)} · {r.via === 'portal' ? 'from the Student Portal' : 'from their computer'}. Ending school turns monitoring off until tomorrow’s
              schedule.
            </span>
          </span>
          <span className="row-gap">
            <button className="btn small primary" disabled={busy === r.childId} onClick={() => answer(r.childId, r.childName, true)}>
              End school for today
            </button>
            <button className="btn small" disabled={busy === r.childId} onClick={() => answer(r.childId, r.childName, false)}>
              Not yet
            </button>
          </span>
        </div>
      ))}
    </section>
  );
}

/** Drill-down for the assignment cards (#330): each item, by child, opening that assignment in the child's planner (#358). */
function DueSheet({ kind, due, onClose }: { kind: 'due' | 'overdue'; due: DueAssignments; onClose: () => void }) {
  const navigate = useNavigate();
  const items = kind === 'due' ? due.dueToday : due.overdue;
  return (
    <Sheet title={kind === 'due' ? 'Assignments due today' : 'Overdue assignments'} onClose={onClose}>
      {!items.length ? (
        <p className="muted">{kind === 'due' ? 'Nothing left to do today.' : 'Nothing overdue. Nice work!'}</p>
      ) : (
        <ul className="inbox-list">
          {items.map((i) => (
            <li key={i.id}>
              <button className="inbox-row" onClick={() => (onClose(), navigate(`/kids/${i.childId}/planner?item=${encodeURIComponent(i.id)}`))}>
                <Avatar name={i.childName} color={i.childColor} size={36} />
                <span className="grow">
                  <strong>{i.title}</strong>
                  <span className="muted small block">
                    {i.childName}
                    {i.subject ? ` · ${i.subject}` : ''}
                    {kind === 'overdue' ? ` · due ${dayLabel(new Date(`${i.dueDate}T00:00:00`).getTime())}` : ''}
                  </span>
                </span>
                <ChevronRight size={18} className="muted" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}

/** Drill-down for "In school now" and "Devices online" (#330). */
function FamilySheet({ kind, onClose }: { kind: 'school' | 'devices'; onClose: () => void }) {
  const { children } = useParent();
  const navigate = useNavigate();
  return (
    <Sheet title={kind === 'school' ? 'School time' : 'Devices'} onClose={onClose}>
      <ul className="inbox-list">
        {children.map((c) => (
          <li key={c.id}>
            <button className="inbox-row" onClick={() => (onClose(), navigate(`/kids/${c.id}/${kind === 'school' ? 'live' : 'devices'}`))}>
              <Avatar name={c.name} color={c.color} size={36} />
              <span className="grow">
                <strong>{c.name}</strong>
                {kind === 'school' ? (
                  <span className="block">
                    <PolicyBadge policy={c.policy} />
                  </span>
                ) : c.devices.length ? (
                  <span className="muted small block">
                    {c.devices.map((d) => `${d.name} (${d.online ? 'online' : 'offline'})`).join(' · ')}
                  </span>
                ) : (
                  <span className="muted small block">No devices paired yet</span>
                )}
              </span>
              <ChevronRight size={18} className="muted" />
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

/** Drill-down for the unread messages tile (#169): who wrote. */
function InboxSheet({ inbox, onClose }: { inbox: Inbox; onClose: () => void }) {
  const navigate = useNavigate();
  const ago = (ts: number) => {
    const m = Math.round((Date.now() - ts) / 60_000);
    return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : clock(ts);
  };
  return (
    <Sheet title="Unread messages" onClose={onClose}>
      {inbox.chats.length === 0 ? (
        <p className="muted">You’re all caught up.</p>
      ) : (
        <ul className="inbox-list">
          {inbox.chats.map((c) => (
            <li key={c.childId}>
              <button className="inbox-row" onClick={() => (onClose(), navigate(`/kids/${c.childId}/chat`))}>
                <Avatar name={c.childName} color={c.childColor} size={36} />
                <span className="grow">
                  <strong>{c.childName}</strong> <span className="inbox-count">{c.count}</span>
                  <span className="muted small block inbox-preview">“{c.lastText}”</span>
                </span>
                <span className="muted small">{ago(c.lastTs)}</span>
                <ChevronRight size={18} className="muted" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}

function RecentActivity() {
  const { socket } = useParent();
  const [items, setItems] = useState<FeedActivity[] | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    api
      .get<FeedActivity[]>('/api/activity?limit=12')
      .then((r) => live && setItems(r))
      .catch(() => live && setItems((cur) => cur ?? []));
    return () => {
      live = false;
    };
  }, [tick]);
  // Refresh as things happen (e.g. a help request, #232) instead of only on page load.
  // Debounced: a burst of website visits refetches once.
  useEffect(() => {
    let timer = 0;
    const off = socket?.on('activity', () => {
      clearTimeout(timer);
      timer = window.setTimeout(() => setTick((t) => t + 1), 1000);
    });
    return () => {
      clearTimeout(timer);
      off?.();
    };
  }, [socket]);

  if (items === null) return null; // first load: keep the layout quiet
  let lastDay = '';
  return (
    <section className="card activity-feed">
      <h2 className="with-icon">
        <ActivityIcon size={18} /> Recent activity
      </h2>
      {!items.length ? (
        <p className="muted small">
          Nothing yet. Websites and alerts appear here once a device is paired and it’s school time.
        </p>
      ) : (
        <ol className="timeline">
          {items.map((i) => {
            const day = dayLabel(i.ts);
            const header = day !== lastDay ? <li key={`d${i.id}`} className="day">{(lastDay = day)}</li> : null;
            return [
              header,
              <li key={i.id} className={`event event-${i.kind}`}>
                <span className="event-icon">{FEED_ICONS[i.kind] ?? <Globe size={15} />}</span>
                <div className="grow">
                  <div className="event-title">{i.title}</div>
                  {activityDetail(i.kind, i.detail) && <div className="muted small">{activityDetail(i.kind, i.detail)}</div>}
                </div>
                <span className="feed-chip" style={{ ['--kid' as string]: i.childColor }}>
                  {i.childName}
                </span>
                <time className="muted small feed-time">{clock(i.ts)}</time>
              </li>,
            ];
          })}
        </ol>
      )}
    </section>
  );
}

/** A computer on a child's card, with the browser extensions its app set up folded under it (#675). */
function ComputerLine({ device, extensions }: { device: Device; extensions: Device[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <li>
        <span className={`dot ${device.online ? 'on' : ''}`} />
        <span className="grow">
          {device.name}
          {extensions.length > 0 && !open && <span className="muted small"> · {extensions.length === 1 ? extensions[0]!.name : `${extensions.length} extensions`}</span>}
        </span>
        <Sharing device={device} />
        {extensions.length > 0 && (
          <button
            type="button"
            className="icon-btn"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} ${device.name}’s browser extensions`}
            onClick={() => setOpen(!open)}
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
      </li>
      {open &&
        extensions.map((e) => (
          <li key={e.id} className="device-sub">
            <Puzzle size={14} aria-hidden />
            <span className="grow">{e.name}</span>
            <span className={`muted small ${extOnline(e) ? 'on-text' : ''}`}>{extOnline(e) ? 'Online' : 'Offline'}</span>
          </li>
        ))}
    </>
  );
}

function KidCard({ child }: { child: Child }) {
  // A records-only plan (#564) has no computer to show.
  const hasComputer = planHas(child, 'computer');
  const groups = groupDevices(child.devices);
  return (
    <article className="kid card" style={{ ['--kid' as string]: kidColor(child.color) }}>
      <Link to={`/kids/${child.id}`} className="kid-head">
        <Avatar name={child.name} color={child.color} />
        <div className="grow">
          <h2>{child.name}</h2>
          {hasComputer && <PolicyBadge policy={child.policy} />}
        </div>
        <ChevronRight size={20} className="muted" />
      </Link>

      <ComplianceNote child={child} />
      {!hasComputer && <p className="muted small">Records only: planner, grades, attendance and reading. No app on {child.name}’s computer.</p>}
      {hasComputer && (
        <ul className="device-lines">
        {!child.devices.length && <li className="muted">No devices yet. Open {child.name}’s page to pair one.</li>}
        {groups.map(({ device: d, extensions }) =>
          d.kind === 'agent' ? (
            <ComputerLine key={d.id} device={d} extensions={extensions} />
          ) : (
            <li key={d.id}>
              <span className={`dot ${extOnline(d) ? 'on' : ''}`} />
              <span className="grow">{d.name}</span>
              <span className="muted small">{child.policy.blockedSites.length} blocked</span>
            </li>
          ),
        )}
        </ul>
      )}

      {hasComputer && (
        <div className="kid-foot">
          <QuickActions child={child} compact />
        </div>
      )}
    </article>
  );
}

function AddChildSheet({ onClose }: { onClose: () => void }) {
  const { refresh, toast } = useParent();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]!);
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true);
    try {
      const child = await api.post<Child>('/api/children', { name, color });
      await refresh();
      onClose();
      navigate(`/kids/${child.id}/devices`);
    } catch (e) {
      const text = (e as Error).message;
      // Past the plan's children (#633): straight to Plan & billing.
      toast({ text, tone: 'alert', ...(/Plan & billing/.test(text) ? { action: { label: 'Plan & billing', run: () => navigate('/settings#plan') } } : {}) });
      setBusy(false);
    }
  }

  return (
    <Sheet title="Add a child" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} autoFocus placeholder="First name" />
        </label>
        <ColorPicker value={color} onChange={setColor} />
        <p className="muted small">School hours start as weekdays, 8:30 to 3:00. You can change them next.</p>
        <button className="btn primary big" disabled={busy || !name.trim()}>
          Add {name.trim() || 'child'}
        </button>
      </form>
    </Sheet>
  );
}

export function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <fieldset className="colors">
      <legend>Color</legend>
      {COLORS.map((c) => (
        <button
          key={c}
          type="button"
          className={`swatch ${c === value ? 'picked' : ''}`}
          style={{ background: kidColor(c) }}
          onClick={() => onChange(c)}
          aria-label={`Color ${c}`}
          aria-pressed={c === value}
        />
      ))}
    </fieldset>
  );
}
